import React, { useState, useEffect, useRef } from "react";
import { getClasses, addLabel, updateDatasetYaml, downloadAnnotationsZip, processImage, compressImage, getVerificationTickets, YOLO26_CLASSES } from "../api";
import VerifiedReportsModal from "./VerifiedReportsModal";

let cachedClasses = null;

export default function AnnotationStudio({ onBack, onToggleSidebar, isSidebarOpen, onLogout }) {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  const [userTickets, setUserTickets] = useState([]);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);

  const loadUserTickets = React.useCallback(async () => {
    const sessionId = localStorage.getItem("cropscan_session_id");
    if (!sessionId) return;
    try {
      const res = await getVerificationTickets(sessionId);
      if (res.status === "success") setUserTickets(res.tickets || []);
    } catch (err) {
      console.warn("Failed to load user tickets", err);
    }
  }, []);

  useEffect(() => {
    loadUserTickets();
  }, [loadUserTickets]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);
  // Step: "setup" or "studio"
  const [step, setStep] = useState("setup");
  const [classSetupMode, setClassSetupMode] = useState("backend"); // backend, existing, custom
  const [customClassesText, setCustomClassesText] = useState("");
  
  // Studio states (default to YOLO26n 26 classes)
  const [classes, setClasses] = useState(YOLO26_CLASSES);
  const [selectedClass, setSelectedClass] = useState(YOLO26_CLASSES[0]);
  const [newLabelInput, setNewLabelInput] = useState("");
  const [activeTool, setActiveTool] = useState("select"); // select, rect, polygon, resize
  const [currentPolygonPoints, setCurrentPolygonPoints] = useState([]);
  const [isDrawingBox, setIsDrawingBox] = useState(false);
  const [boxStartCoords, setBoxStartCoords] = useState(null);
  const [boxCurrentCoords, setBoxCurrentCoords] = useState(null);
  const [selectedAnnotationIndex, setSelectedAnnotationIndex] = useState(null);
  const [resizingBoxIndex, setResizingBoxIndex] = useState(null);
  const [resizingHandle, setResizingHandle] = useState(null);

  useEffect(() => {
    setCurrentPolygonPoints([]);
    setIsDrawingBox(false);
    setBoxStartCoords(null);
    setBoxCurrentCoords(null);
    setResizingBoxIndex(null);
    setResizingHandle(null);
  }, [activeTool]);

  // Single image annotation states
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingImage, setProcessingImage] = useState(null);
  const [result, setResult] = useState(null);
  const [customAnnotations, setCustomAnnotations] = useState([]);

  // YOLO & AI Annotation states
  const [yoloConf, setYoloConf] = useState(0.08);
  const [yoloAutoDetect, setYoloAutoDetect] = useState(true);
  const [isDetecting, setIsDetecting] = useState(false);
  const currentFileRef = useRef(null);
  const folderFilesRef = useRef([]);

  // Folder annotation states
  const [isFolderMode, setIsFolderMode] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const anotherFileInputRef = useRef(null);
  const anotherFolderInputRef = useRef(null);
  const [folderResults, setFolderResults] = useState([]);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);

  const [history, setHistory] = useState([]);

  const imageRef = useRef(null);

  useEffect(() => {
    setSelectedAnnotationIndex(null);
  }, [activeIndex, result]);

  // Zoom & Pan states
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  useEffect(() => {
    loadClasses();

    // Load history from localStorage
    const savedHistory = localStorage.getItem("annotation_history");
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory).slice(0, 3));
      } catch (e) {}
    }

    // Load active session from sessionStorage for page reloads
    const savedSession = sessionStorage.getItem("annotation_prediction_session");
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        if (session.step) setStep(session.step);
        if (session.result) {
          setResult(session.result);
          setCustomAnnotations(session.result.detections || []);
        }
        if (session.isFolderMode) setIsFolderMode(true);
        if (session.folderResults && session.folderResults.length > 0) {
          setFolderResults(session.folderResults);
          setActiveIndex(session.activeIndex || 0);
        }
      } catch (e) {}
    }
  }, []);

  const persistSession = async (currentResult, folderMode, folderRes, idx) => {
    try {
      let compressedResult = currentResult;
      if (currentResult) {
        let compImg = currentResult.image;
        let compOverlay = currentResult.overlay_b64;
        try {
          if (compImg) compImg = await compressImage(compImg, 600, 600, 0.7);
          if (compOverlay) compOverlay = await compressImage(compOverlay, 600, 600, 0.7);
        } catch (_) {}
        compressedResult = {
          ...currentResult,
          image: compImg,
          overlay_b64: compOverlay
        };
      }

      let compressedFolderRes = folderRes;
      if (folderRes && folderRes.length > 0) {
        compressedFolderRes = await Promise.all(folderRes.map(async (res) => {
          try {
            let comp = res.image;
            let compOverlay = res.overlay_b64;
            if (comp) comp = await compressImage(comp, 400, 400, 0.6);
            if (compOverlay) compOverlay = await compressImage(compOverlay, 400, 400, 0.6);
            return { ...res, image: comp, overlay_b64: compOverlay };
          } catch (_) {
            return res;
          }
        }));
      }

      sessionStorage.setItem("annotation_prediction_session", JSON.stringify({
        step: "studio",
        result: compressedResult,
        isFolderMode: folderMode,
        folderResults: compressedFolderRes,
        activeIndex: idx
      }));
    } catch (e) {}
  };

  const clearSession = () => {
    try {
      sessionStorage.removeItem("annotation_prediction_session");
    } catch (e) {}
  };

  const saveToHistory = async (filename, detections, imgData) => {
    let thumb = imgData;
    try {
      thumb = await compressImage(imgData);
    } catch (e) {
      console.warn("Image compression failed", e);
    }

    const newEntry = {
      id: `act-${Date.now()}`,
      filename,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
      size: "1.2 MB",
      thumbnail: thumb,
      detections
    };
    const updated = [newEntry, ...history.slice(0, 2)];
    setHistory(updated);
    try {
      localStorage.setItem("annotation_history", JSON.stringify(updated));
    } catch (e) {
      console.warn("LocalStorage write failed", e);
    }
  };

  const saveFolderToHistory = async (folderResults) => {
    if (!folderResults || folderResults.length === 0) return;
    const lastThree = folderResults.slice(-3).reverse();
    const newEntries = await Promise.all(
      lastThree.map(async (item) => {
        let thumb = item.image;
        try {
          thumb = await compressImage(item.image);
        } catch (e) {}
        return {
          id: `act-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          filename: item.filename,
          date: new Date().toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }),
          size: "1.5 MB",
          thumbnail: thumb,
          detections: item.detections
        };
      })
    );

    setHistory((prev) => {
      const updated = [...newEntries, ...prev].slice(0, 3);
      try {
        localStorage.setItem("annotation_history", JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const getImageSrc = (imgStr) => {
    if (!imgStr) return "";
    if (imgStr.startsWith("data:") || imgStr.startsWith("http")) return imgStr;
    return `data:image/jpeg;base64,${imgStr}`;
  };

  const loadClasses = async () => {
    if (cachedClasses && cachedClasses.length > 0) {
      setClasses(cachedClasses);
      setSelectedClass(cachedClasses[0]);
      return;
    }
    try {
      const res = await getClasses();
      const loaded = (res && res.classes && res.classes.length > 0) ? res.classes : YOLO26_CLASSES;
      setClasses(loaded);
      if (loaded.length > 0) {
        setSelectedClass(loaded[0]);
        cachedClasses = loaded;
      }
    } catch (err) {
      console.warn("Failed to load classes from server, defaulting to YOLO26_CLASSES", err);
      setClasses(YOLO26_CLASSES);
      setSelectedClass(YOLO26_CLASSES[0]);
    }
  };

  const handleStartSetup = () => {
    if (classSetupMode === "custom" && customClassesText.trim()) {
      const parsed = customClassesText.split(",")
        .map(c => c.trim())
        .filter(c => c.length > 0);
      if (parsed.length > 0) {
        setClasses(parsed);
        setSelectedClass(parsed[0]);
        cachedClasses = parsed;
      }
    }
    setStep("studio");
  };

  const handleAddLabel = async () => {
    if (!newLabelInput.trim()) return;
    try {
      await addLabel(newLabelInput.trim());
      const label = newLabelInput.trim();
      setClasses(prev => {
        const next = [...prev.filter(c => c !== label), label];
        cachedClasses = next;
        return next;
      });
      setSelectedClass(label);
      setNewLabelInput("");
      alert(`Class "${label}" added successfully.`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateYaml = async () => {
    try {
      await updateDatasetYaml();
      alert("dataset.yaml updated successfully.");
    } catch (err) {
      alert("Failed to update dataset.yaml");
    }
  };

  const handleDownloadZip = async () => {
    try {
      const blob = await downloadAnnotationsZip();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "annotations.zip";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download annotations zip file.");
    }
  };

  const handleUploadSingleImage = async (file) => {
    if (!file) return;
    currentFileRef.current = file;
    handleStartSetup();
    setLoading(true);

    let localDataUrl = "";
    try {
      localDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      setProcessingImage(localDataUrl);
    } catch (e) {
      console.warn("FileReader error", e);
    }

    try {
      let detections = [];
      let imgDisplay = localDataUrl;
      if (yoloAutoDetect) {
        const res = await processImage(file, {}, null, yoloConf);
        detections = (res && res.detections) ? res.detections : [];
        if (res && res.overlay_b64 && res.overlay_b64.length > 100) {
          imgDisplay = res.overlay_b64;
        }
        if (detections.length > 0) {
          const detectedLabels = detections.map(d => d.label).filter(Boolean);
          setClasses(prev => [...new Set([...prev, ...detectedLabels])]);
        }
      }

      const resultEntry = {
        filename: file.name,
        image: imgDisplay || localDataUrl,
        detections: detections
      };
      setResult(resultEntry);
      setCustomAnnotations(detections);
      persistSession(resultEntry, false, [], 0);
      await saveToHistory(file.name, detections, imgDisplay || localDataUrl);
    } catch (err) {
      console.warn("processImage failed", err);
      const resultEntry = {
        filename: file.name,
        image: localDataUrl,
        detections: []
      };
      setResult(resultEntry);
      setCustomAnnotations([]);
      persistSession(resultEntry, false, [], 0);
      await saveToHistory(file.name, [], localDataUrl);
    } finally {
      setLoading(false);
      setProcessingImage(null);
    }
  };

  const handleFolderUpload = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith("image/"));
    if (files.length === 0) {
      alert("No image files found in selected folder.");
      return;
    }
    folderFilesRef.current = files;
    handleStartSetup();
    setIsFolderMode(true);
    setActiveIndex(0);
    setIsBatchProcessing(true);
    setBatchProgress(0);

    const initialResults = [];
    for (let i = 0; i < files.length; i++) {
      let localDataUrl = "";
      try {
        localDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(files[i]);
        });
      } catch (err) {}

      try {
        let detections = [];
        let imgDisplay = localDataUrl;
        if (yoloAutoDetect) {
          const res = await processImage(files[i], {}, null, yoloConf);
          detections = (res && res.detections) ? res.detections : [];
          if (res && res.overlay_b64 && res.overlay_b64.length > 100) {
            imgDisplay = res.overlay_b64;
          }
          if (detections.length > 0) {
            const detectedLabels = detections.map(d => d.label).filter(Boolean);
            setClasses(prev => [...new Set([...prev, ...detectedLabels])]);
          }
        }
        const cleanName = (files[i].name || `image_${i}.jpg`).split("/").pop().split("\\").pop();
        initialResults.push({
          filename: cleanName,
          image: imgDisplay || localDataUrl,
          detections: detections
        });
      } catch (err) {
        const cleanName = (files[i].name || `image_${i}.jpg`).split("/").pop().split("\\").pop();
        initialResults.push({
          filename: cleanName,
          image: localDataUrl,
          detections: []
        });
      }
      setFolderResults([...initialResults]);
      setBatchProgress(Math.round(((i + 1) / files.length) * 100));
    }
    setIsBatchProcessing(false);
    if (initialResults.length > 0) {
      setResult(initialResults[0]);
      setCustomAnnotations(initialResults[0].detections || []);
      persistSession(initialResults[0], true, initialResults, 0);
      await saveFolderToHistory(initialResults);
    }
  };

  const handleRunYolo = async (customThreshold) => {
    const threshold = customThreshold !== undefined ? customThreshold : yoloConf;
    setIsDetecting(true);
    try {
      let fileToProcess = isFolderMode ? folderFilesRef.current[activeIndex] : currentFileRef.current;
      if (!fileToProcess && result && result.image) {
        try {
          const fetchRes = await fetch(getImageSrc(result.image));
          const blob = await fetchRes.blob();
          fileToProcess = new File([blob], result.filename || "image.jpg", { type: blob.type || "image/jpeg" });
        } catch (e) {
          console.warn("Could not fetch image blob", e);
        }
      }

      if (!fileToProcess) {
        alert("Please upload an image first to run YOLO detection.");
        setIsDetecting(false);
        return;
      }

      const res = await processImage(fileToProcess, {}, selectedClass, threshold);
      const detections = (res && res.detections) ? res.detections : [];
      setCustomAnnotations(detections);
      if (detections.length > 0) {
        const detectedLabels = detections.map(d => d.label).filter(Boolean);
        setClasses(prev => [...new Set([...prev, ...detectedLabels])]);
      }
      let imgDisplay = (res && res.overlay_b64 && res.overlay_b64.length > 100) ? res.overlay_b64 : result.image;
      const updatedEntry = {
        ...result,
        image: imgDisplay,
        detections: detections
      };
      setResult(updatedEntry);
      persistSession(updatedEntry, isFolderMode, isFolderMode ? folderResults : [], activeIndex);
      if (isFolderMode) {
        const updatedFolder = [...folderResults];
        updatedFolder[activeIndex] = updatedEntry;
        setFolderResults(updatedFolder);
      }
    } catch (err) {
      console.error("YOLO detection failed", err);
      alert("YOLO auto-detection failed.");
    } finally {
      setIsDetecting(false);
    }
  };

  const handleResizeMouseDown = (e, idx, handle) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingBoxIndex(idx);
    setResizingHandle(handle);
    setSelectedAnnotationIndex(idx);
  };

  const handleMouseDown = (e) => {
    if (activeTool === "resize") return;
    if (activeTool !== "rect") return;
    if (!imageRef.current) return;
    e.preventDefault();
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setIsDrawingBox(true);
    setBoxStartCoords([x, y]);
    setBoxCurrentCoords([x, y]);
  };

  const handleMouseMove = (e) => {
    if (resizingBoxIndex !== null && resizingHandle !== null) {
      const rect = imageRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      const updated = [...customAnnotations];
      const box = [...updated[resizingBoxIndex].box];

      if (resizingHandle === 'tl') {
        box[0] = clampedX;
        box[1] = clampedY;
      } else if (resizingHandle === 'tr') {
        box[2] = clampedX;
        box[1] = clampedY;
      } else if (resizingHandle === 'bl') {
        box[0] = clampedX;
        box[3] = clampedY;
      } else if (resizingHandle === 'br') {
        box[2] = clampedX;
        box[3] = clampedY;
      }

      updated[resizingBoxIndex] = { ...updated[resizingBoxIndex], box };
      setCustomAnnotations(updated);
      if (isFolderMode) {
        const updatedResults = [...folderResults];
        updatedResults[activeIndex].detections = updated;
        setFolderResults(updatedResults);
      }
      return;
    }

    if (activeTool !== "rect" || !isDrawingBox || !boxStartCoords) return;
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setBoxCurrentCoords([x, y]);
  };

  const handleMouseUp = (e) => {
    if (resizingBoxIndex !== null) {
      const updated = [...customAnnotations];
      const box = [...updated[resizingBoxIndex].box];
      const x1 = Math.min(box[0], box[2]);
      const y1 = Math.min(box[1], box[3]);
      const x2 = Math.max(box[0], box[2]);
      const y2 = Math.max(box[1], box[3]);
      
      updated[resizingBoxIndex] = { ...updated[resizingBoxIndex], box: [x1, y1, x2, y2] };
      setCustomAnnotations(updated);
      if (isFolderMode) {
        const updatedResults = [...folderResults];
        updatedResults[activeIndex].detections = updated;
        setFolderResults(updatedResults);
      }
      setResizingBoxIndex(null);
      setResizingHandle(null);
      return;
    }

    if (activeTool !== "rect" || !isDrawingBox || !boxStartCoords || !boxCurrentCoords) return;
    setIsDrawingBox(false);
    const x1 = Math.min(boxStartCoords[0], boxCurrentCoords[0]);
    const y1 = Math.min(boxStartCoords[1], boxCurrentCoords[1]);
    const x2 = Math.max(boxStartCoords[0], boxCurrentCoords[0]);
    const y2 = Math.max(boxStartCoords[1], boxCurrentCoords[1]);
    const w = x2 - x1;
    const h = y2 - y1;

    if (w > 1 && h > 1) {
      const newBox = {
        label: selectedClass || "Leaf",
        confidence: 1.0,
        box: [x1, y1, x2, y2]
      };
      const updated = [...customAnnotations, newBox];
      setCustomAnnotations(updated);
      setSelectedAnnotationIndex(updated.length - 1);
      if (isFolderMode) {
        const updatedResults = [...folderResults];
        updatedResults[activeIndex].detections = updated;
        setFolderResults(updatedResults);
      }
    }
    setBoxStartCoords(null);
    setBoxCurrentCoords(null);
  };

  // Click on image container to add dynamic bounding boxes (Interactive Annotation)
  const handleImageClick = (e) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (activeTool === "polygon") {
      // Check if clicking near the first point to close the polygon
      if (currentPolygonPoints.length >= 3) {
        const [firstX, firstY] = currentPolygonPoints[0];
        const dist = Math.sqrt((x - firstX) ** 2 + (y - firstY) ** 2);
        if (dist < 3) { // 3% distance threshold to close
          completePolygon();
          return;
        }
      }
      setCurrentPolygonPoints(prev => [...prev, [x, y]]);
    }
  };

  const completePolygon = (pointsToUse) => {
    const pts = pointsToUse || currentPolygonPoints;
    if (pts.length < 3) {
      alert("A polygon must have at least 3 points.");
      return;
    }

    const xs = pts.map(p => p[0]);
    const ys = pts.map(p => p[1]);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs);
    const maxY = Math.max(...ys);

    const newPolygon = {
      label: selectedClass || "Leaf",
      confidence: 1.0,
      box: [minX, minY, maxX, maxY],
      points: pts
    };

    const updated = [...customAnnotations, newPolygon];
    setCustomAnnotations(updated);
    setCurrentPolygonPoints([]);

    if (isFolderMode) {
      const updatedResults = [...folderResults];
      updatedResults[activeIndex].detections = updated;
      setFolderResults(updatedResults);
    }
  };

  // Export current single JSON annotation
  const downloadJSON = () => {
    if (!result) return;
    const annotationData = {
      filename: result.filename,
      annotations: customAnnotations.map(a => ({
        label: a.label,
        box: a.box,
        confidence: a.confidence,
        points: a.points || null
      }))
    };
    const blob = new Blob([JSON.stringify(annotationData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${result.filename.split(".")[0]}_annotation.json`;
    a.click();
  };

  // Export current single CSV annotation
  const downloadCSV = () => {
    if (!result) return;
    let csvContent = "Filename,Label,Box_Left_Pct,Box_Top_Pct,Box_Width_Pct,Box_Height_Pct,Confidence\n";
    customAnnotations.forEach(a => {
      const box = a.box || [20, 30, 45, 60];
      csvContent += `"${result.filename}","${a.label}",${box[0]},${box[1]},${box[2]},${box[3]},${a.confidence}\n`;
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${result.filename.split(".")[0]}_annotation.csv`;
    a.click();
  };

  // Download complete batch JSON annotation
  const downloadBatchJSON = () => {
    if (folderResults.length === 0) return;
    const batchData = folderResults.map(r => ({
      filename: r.filename,
      annotations: r.detections.map(a => ({
        label: a.label,
        box: a.box,
        confidence: a.confidence,
        points: a.points || null
      }))
    }));
    const blob = new Blob([JSON.stringify(batchData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "batch_annotations.json";
    a.click();
  };

  // Download complete batch CSV annotation
  const downloadBatchCSV = () => {
    if (folderResults.length === 0) return;
    let csvContent = "Filename,Label,Box_Left_Pct,Box_Top_Pct,Box_Width_Pct,Box_Height_Pct,Confidence\n";
    folderResults.forEach(r => {
      r.detections.forEach(a => {
        const box = a.box || [20, 30, 45, 60];
        csvContent += `"${r.filename}","${a.label}",${box[0]},${box[1]},${box[2]},${box[3]},${a.confidence}\n`;
      });
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "batch_annotations.csv";
    a.click();
  };

  const handleSelectFolderImage = (idx) => {
    setActiveIndex(idx);
    const item = folderResults[idx];
    setResult(item);
    setCustomAnnotations(item.detections || []);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleUploadSingleImage(file);
    }
  };

  return (
    <div className="h-full overflow-y-auto flex flex-col w-full relative bg-surface-bright text-on-surface pb-32 lg:pb-16">
      {/* Header */}
      <header className="sticky top-0 w-full z-40 bg-surface/90 dark:bg-inverse-surface/90 backdrop-blur-md border-b border-outline-variant/30 flex justify-between items-center px-4 sm:px-margin-desktop h-16 shadow-sm shrink-0">
        <div className="flex items-center gap-2 sm:gap-stack-md">
          <button 
            onClick={() => {
              if (step === "studio") {
                setStep("setup");
              } else {
                clearSession();
                onBack();
              }
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-surface-container-low hover:bg-surface-container border border-outline-variant/30 text-on-surface-variant hover:text-primary transition-all duration-200 shrink-0 group text-xs sm:text-sm font-semibold shadow-xs"
            title={step === "studio" ? "Back to configuration" : "Return to Dashboard"}
          >
            <span className="material-symbols-outlined text-[18px] sm:text-[20px] group-hover:-translate-x-0.5 transition-transform duration-200">arrow_back</span>
            <span className="truncate max-w-[80px] sm:max-w-none">{step === "studio" ? "Back" : "Dashboard"}</span>
          </button>
          <div className="h-6 w-px bg-outline-variant/50 mx-1 sm:mx-2 shrink-0"></div>
          {/* <h2 className="font-headline-md text-headline-md text-primary font-semibold truncate">Annotation Studio</h2> */}
        </div>
        <div className="flex items-center gap-stack-md">
          <button className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full">
            <span className="material-symbols-outlined">search</span>
          </button>
          <button 
            onClick={() => setShowVerifiedModal(true)}
            className="text-on-surface-variant hover:text-primary transition-colors duration-250 w-10 h-10 flex items-center justify-center rounded-full relative"
            title="Verified Reports"
          >
            <span className="material-symbols-outlined">notifications</span>
            {(() => {
              const seenTickets = JSON.parse(localStorage.getItem("seen_tickets") || "[]");
              const unreadCount = userTickets.filter((t) => t.status === "Resolved" && !seenTickets.includes(t.ticket_id)).length;
              return unreadCount > 0 ? (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-error text-white text-[10px] font-extrabold rounded-full ring-2 ring-surface flex items-center justify-center">
                  {unreadCount}
                </span>
              ) : null;
            })()}
          </button>
          <div className="relative" ref={profileMenuRef}>
            <button 
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-outline-variant/30 overflow-hidden bg-white hover:border-primary transition-all duration-250 p-1 shadow-sm hover:scale-105"
            >
              <img
                src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                alt="Niruthi Logo"
                className="w-full h-full object-contain"
              />
            </button>
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-inverse-surface border border-outline-variant/30 rounded-xl shadow-lg z-50 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="px-4 py-3 border-b border-outline-variant/20 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border border-outline-variant/30 overflow-hidden bg-white shrink-0 p-0.5 shadow-sm">
                    <img
                      src={process.env.PUBLIC_URL + "/niruthi_logo.png"}
                      alt="User Profile"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-on-surface truncate">Niruthi User</p>
                    <p className="text-xs text-on-surface-variant truncate">Niruthiuser</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 flex items-center gap-2 transition-colors font-medium"
                >
                  <span className="material-symbols-outlined text-[18px]">logout</span>
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* SETUP STEP */}
      {step === "setup" && (
        <div className="p-margin-desktop max-w-6xl mx-auto w-full flex-1 flex flex-col gap-stack-lg">
          {/* Title & Description */}
          <div>
            <h3 className="font-headline-lg text-headline-lg text-on-background mb-2">Annotation Studio</h3>
            <p className="font-body-lg text-body-lg text-on-surface-variant">
              Configure your dataset classes and upload field imagery to start generating labels with AI-assisted Segment Anything (SAM) models.
            </p>
          </div>

          {/* Configuration Card */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/20 p-6 shadow-sm flex flex-col gap-5">
            <h4 className="font-headline-sm text-headline-sm font-semibold text-on-surface border-b border-outline-variant/10 pb-2">
              Class Setup Configuration
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button 
                type="button"
                onClick={() => setClassSetupMode("backend")}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-left flex flex-col gap-1 ${classSetupMode === "backend" ? "border-primary bg-primary/5" : "border-outline-variant/30"}`}
              >
                <div className="font-semibold text-body-md text-on-surface">IP102 Binary Pest Model</div>
                <p className="text-[11px] text-on-surface-variant leading-relaxed">Loads binary pest detection class ('pest'). Additional custom categories can be created as needed.</p>
              </button>
              <button 
                type="button"
                onClick={() => setClassSetupMode("existing")}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-left flex flex-col gap-1 ${classSetupMode === "existing" ? "border-primary bg-primary/5" : "border-outline-variant/30"}`}
              >
                <div className="font-semibold text-body-md text-on-surface">Existing Project Classes</div>
                <p className="text-[11px] text-on-surface-variant leading-relaxed">Import previously saved custom class configuration from localStorage.</p>
              </button>
              <button 
                type="button"
                onClick={() => setClassSetupMode("custom")}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-left flex flex-col gap-1 ${classSetupMode === "custom" ? "border-primary bg-primary/5" : "border-outline-variant/30"}`}
              >
                <div className="font-semibold text-body-md text-on-surface">Create Custom Classes</div>
                <p className="text-[11px] text-on-surface-variant leading-relaxed">Define custom project classes and category entries manually below.</p>
              </button>
            </div>

            {classSetupMode === "custom" && (
              <div className="flex flex-col gap-2 mt-1">
                <label className="font-body-sm font-semibold text-on-surface">Comma-Separated Custom Labels:</label>
                <textarea 
                  rows="2" 
                  placeholder="e.g. Rice-Ear, Stem-Leaf, Brown-Spot"
                  value={customClassesText}
                  onChange={(e) => setCustomClassesText(e.target.value)}
                  className="w-full px-4 py-3 bg-surface border border-outline-variant rounded-xl focus:outline-none focus:border-primary text-body-sm"
                />
              </div>
            )}
          </div>

          {/* AI Model & Detection Configuration Card */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/20 p-6 shadow-sm flex flex-col gap-4">
            <div className="flex items-center justify-between border-b border-outline-variant/10 pb-3">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-[22px]">smart_toy</span>
                <h4 className="font-headline-sm text-headline-sm font-semibold text-on-surface">
                  YOLO IP102 Binary Pest Detector + SAM2
                </h4>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 px-2.5 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  IP102 Pest Detector (best.pt) Active
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-500/10 text-blue-600 px-2.5 py-0.5 rounded-full">
                  SAM2 Segmentation
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="flex flex-col gap-1.5">
                <div className="flex justify-between items-center">
                  <label className="font-body-sm font-semibold text-on-surface">
                    YOLO Detection Confidence Threshold:
                  </label>
                  <span className="text-xs font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                    {(yoloConf * 100).toFixed(0)}% (conf: {yoloConf.toFixed(2)})
                  </span>
                </div>
                <input
                  type="range"
                  min="0.02"
                  max="0.50"
                  step="0.01"
                  value={yoloConf}
                  onChange={(e) => setYoloConf(parseFloat(e.target.value))}
                  className="w-full h-2 bg-outline-variant/50 rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-[10px] text-on-surface-variant font-mono">
                  <span>Sensitive (2%)</span>
                  <span>Recommended (8%)</span>
                  <span>Strict (50%)</span>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl border border-outline-variant/30 bg-surface-container-low/50">
                <div className="flex flex-col">
                  <span className="font-body-sm font-semibold text-on-surface">Auto-Detect on Upload</span>
                  <span className="text-[11px] text-on-surface-variant">Automatically scan with YOLO binary pest detector & segment with SAM2 when images are uploaded.</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={yoloAutoDetect} 
                    onChange={(e) => setYoloAutoDetect(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-outline-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Large Upload Dropzone Area */}
          <div 
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`drop-zone w-full rounded-20 border-2 border-dashed border-outline-variant/60 bg-surface-container-low hover:border-primary-container hover:bg-primary-container/5 flex flex-col items-center justify-center p-12 shadow-[0px_4px_20px_rgba(0,0,0,0.02)] cursor-pointer ${isDragOver ? "dragover" : ""}`}
          >
            <div className="w-16 h-16 rounded-full bg-surface shadow-sm flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-primary text-[32px]">cloud_upload</span>
            </div>
            <h4 className="font-headline-md text-headline-md text-on-background mb-2 text-center">
              Drag and drop imagery here to start labeling
            </h4>
            <p className="font-body-sm text-body-sm text-on-surface-variant mb-8 text-center">
              Configure class setup above and browse your files below.
            </p>
            
            <div className="flex items-center gap-stack-md">
              <button 
                type="button"
                onClick={() => anotherFileInputRef.current?.click()}
                className="px-6 py-2.5 bg-gradient-to-r from-primary to-secondary text-on-primary rounded-lg font-body-sm font-semibold shadow-md hover:shadow-lg transition-all duration-250 flex items-center gap-2 cursor-pointer"
              >
                <span className="material-symbols-outlined text-[18px]">image</span> Upload Image
              </button>
              <button 
                type="button"
                onClick={() => anotherFolderInputRef.current?.click()}
                className="px-6 py-2.5 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container hover:border-primary/50 transition-all duration-250 rounded-lg font-body-sm font-medium flex items-center gap-2 cursor-pointer"
              >
                <span className="material-symbols-outlined text-[18px]">folder</span> Upload Folder
              </button>
            </div>
            <p className="font-label-code text-label-code text-tertiary mt-6">Supported formats: JPG, PNG, TIFF, WEBP</p>
          </div>

          {/* Recent Annotation Activity */}
          {history.length > 0 && (
            <div className="mt-8">
              <h4 className="font-headline-md text-headline-md text-on-background mb-6 font-semibold">Recent Annotation Activity</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-stack-md">
                {history.slice(0, 3).map((item) => (
                  <button 
                    key={item.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      const resEntry = {
                        filename: item.filename,
                        image: item.thumbnail,
                        detections: item.detections || []
                      };
                      setResult(resEntry);
                      setCustomAnnotations(item.detections || []);
                      setStep("studio");
                      persistSession(resEntry, false, [], 0);
                    }}
                    className="bg-surface-container-low hover:bg-surface-container-medium rounded-xl p-4 shadow-sm hover:shadow-md border border-outline-variant/20 hover:border-primary/50 transition-all flex flex-col group cursor-pointer text-left w-full"
                  >
                    <div className="w-full h-36 rounded-lg overflow-hidden mb-3 relative bg-surface-container">
                      <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" src={getImageSrc(item.thumbnail)} alt={item.filename}/>
                    </div>
                    <h5 className="font-body-md text-body-md font-semibold text-on-background truncate w-full" title={item.filename}>{item.filename}</h5>
                    <div className="flex items-center justify-between mt-2 w-full">
                      <span className="font-label-code text-[11px] text-on-surface-variant">{item.date}</span>
                      <span className="text-[10px] bg-primary/10 text-primary px-2.5 py-0.5 rounded-full font-semibold uppercase">{item.detections?.length || 0} Boxes</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* STUDIO WORKSPACE */}
      {step === "studio" && (
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Main Area: Toolbar, Canvas, Carousel */}
          <div className="flex-1 flex flex-col p-gutter gap-gutter overflow-hidden bg-surface-bright relative">
            
            {/* Toolbar floating */}
            <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 p-1 bg-surface/90 backdrop-blur-xl border border-outline-variant/40 rounded-lg shadow-sm">
              <button 
                onClick={() => setActiveTool("select")}
                className={`p-2 rounded-md transition-colors ${activeTool === "select" ? "bg-primary/10 text-primary font-bold" : "text-on-surface-variant hover:bg-surface-variant"}`}
                title="Select / Move"
              >
                <span className="material-symbols-outlined text-[20px]">near_me</span>
              </button>
              <button 
                onClick={() => setActiveTool("rect")}
                className={`p-2 rounded-md transition-colors ${activeTool === "rect" ? "bg-primary/10 text-primary font-bold" : "text-on-surface-variant hover:bg-surface-variant"}`}
                title="Rectangle Tool (R) - Drag on image to draw bounding boxes"
              >
                <span className="material-symbols-outlined text-[20px]">crop_square</span>
              </button>
              <button 
                onClick={() => setActiveTool("polygon")}
                className={`p-2 rounded-md transition-colors ${activeTool === "polygon" ? "bg-primary/10 text-primary font-bold" : "text-on-surface-variant hover:bg-surface-variant"}`}
                title="Polygon Tool (P)"
              >
                <span className="material-symbols-outlined text-[20px]">polyline</span>
              </button>
              <button 
                onClick={() => setActiveTool("resize")}
                className={`p-2 rounded-md transition-colors ${activeTool === "resize" ? "bg-primary/10 text-primary font-bold" : "text-on-surface-variant hover:bg-surface-variant"}`}
                title="Resize Box Tool (S) - Click and drag corners to resize annotations"
              >
                <span className="material-symbols-outlined text-[20px]">open_in_full</span>
              </button>
              {activeTool === "polygon" && currentPolygonPoints.length >= 3 && (
                <>
                  <div className="w-px h-6 bg-outline-variant/30 mx-1"></div>
                  <button 
                    onClick={() => completePolygon()}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary text-on-primary hover:bg-primary/95 text-xs font-semibold transition-all shadow-xs"
                    title="Finish drawing polygon shape"
                  >
                    <span className="material-symbols-outlined text-[14px]">done</span> Complete
                  </button>
                  <button 
                    onClick={() => setCurrentPolygonPoints([])}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-md border border-outline text-on-surface hover:bg-surface-variant text-xs font-semibold transition-all"
                    title="Cancel drawing"
                  >
                    <span className="material-symbols-outlined text-[14px]">close</span> Cancel
                  </button>
                </>
              )}
              <div className="w-px h-6 bg-outline-variant/30 mx-1"></div>
              <button 
                onClick={() => handleRunYolo()}
                disabled={isDetecting || loading}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all text-xs font-semibold ${
                  isDetecting 
                    ? "bg-amber-500/20 text-amber-600 animate-pulse cursor-wait" 
                    : "bg-primary/10 text-primary hover:bg-primary/20"
                }`}
                title="Run IP102 YOLO Pest Detection + SAM2 Segmentation (best.pt)"
              >
                <span className="material-symbols-outlined text-[16px] text-amber-500">
                  {isDetecting ? "sync" : "bolt"}
                </span>
                <span>{isDetecting ? "Detecting..." : "YOLO Detect"}</span>
              </button>
              <div className="w-px h-6 bg-outline-variant/30 mx-1"></div>
              <button onClick={() => setScale(prev => Math.min(prev + 0.2, 3))} className="p-2 rounded-md text-on-surface-variant hover:bg-surface-variant" title="Zoom In"><span className="material-symbols-outlined text-[20px]">zoom_in</span></button>
              <button onClick={() => setScale(prev => Math.max(prev - 0.2, 0.5))} className="p-2 rounded-md text-on-surface-variant hover:bg-surface-variant" title="Zoom Out"><span className="material-symbols-outlined text-[20px]">zoom_out</span></button>
              <button onClick={() => { setScale(1); setPan({ x: 0, y: 0 }); }} className="p-2 rounded-md text-on-surface-variant hover:bg-surface-variant" title="Fit to Screen"><span className="material-symbols-outlined text-[20px]">center_focus_strong</span></button>
            </div>

            {/* Canvas / Image container */}
            <div className="flex-1 relative rounded-xl overflow-hidden border border-outline-variant/40 bg-surface-container-low shadow-[0px_4px_20px_rgba(0,0,0,0.02)] flex flex-col items-center justify-center">
              {loading && processingImage ? (
                // Laser horizontal scanner line animation overlay for single image upload
                <div className="w-full h-full min-h-[460px] overflow-hidden relative flex flex-col items-center justify-center bg-black/70 rounded-xl">
                  <img 
                    src={getImageSrc(processingImage)} 
                    alt="Scanning preview" 
                    className="max-h-[460px] object-contain filter brightness-[0.6]" 
                  />
                  <div className="absolute left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent shadow-[0_0_10px_#4caf50] animate-scan z-10"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 z-20 text-white gap-3">
                    <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                    <p className="font-headline-sm text-headline-sm font-semibold tracking-wide drop-shadow-md">Generating AI Annotations & Bounding Boxes...</p>
                  </div>
                </div>
              ) : isBatchProcessing && !result ? (
                // Folder batch processing progress loader overlay
                <div className="w-full h-full min-h-[460px] flex flex-col items-center justify-center bg-surface-container-low p-8 text-center">
                  <div className="w-14 h-14 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4"></div>
                  <h4 className="font-headline-md text-headline-md text-on-surface mb-2 font-semibold">Processing Batch Folder Imagery...</h4>
                  <p className="font-body-sm text-on-surface-variant mb-6">{batchProgress}% Completed</p>
                  <div className="w-full max-w-md bg-surface-variant h-3 rounded-full overflow-hidden shadow-inner">
                    <div className="bg-primary h-full rounded-full transition-all duration-300" style={{ width: `${batchProgress}%` }}></div>
                  </div>
                </div>
              ) : !result ? (
                // State 1 Dropzone inside canvas
                <div 
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`absolute inset-0 flex flex-col items-center justify-center p-8 cursor-pointer border-2 border-dashed border-outline-variant/30 bg-surface-container-low overflow-y-auto ${isDragOver ? "dragover" : ""}`}
                >
                  <span className="material-symbols-outlined text-4xl text-outline-variant mb-4">draw</span>
                  <p className="font-body-sm font-semibold text-on-surface-variant mb-1">Drop files or click below to launch studio canvas</p>
                  <p className="font-label-code text-label-code text-tertiary mb-6">Select a single image or an entire folder of crop images to annotate</p>
                  <div className="flex gap-4 mb-8">
                    <label className="px-6 py-2.5 bg-gradient-to-r from-primary to-secondary text-on-primary font-body-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-250 flex items-center gap-2 cursor-pointer">
                      <span className="material-symbols-outlined text-[18px]">image</span> Select Image
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadSingleImage(e.target.files[0])} />
                    </label>
                    <label className="px-6 py-2.5 bg-transparent border border-outline-variant text-on-surface hover:bg-surface-container transition-all duration-250 rounded-lg font-body-sm font-medium flex items-center gap-2 cursor-pointer">
                      <span className="material-symbols-outlined text-[18px]">folder</span> Select Folder
                      <input type="file" webkitdirectory="true" directory="true" multiple className="hidden" onChange={handleFolderUpload} />
                    </label>
                  </div>

                  {/* Recent Activity in Studio */}
                  {history.length > 0 && (
                    <div className="w-full max-w-xl mt-4">
                      <h4 className="font-headline-sm text-headline-sm text-on-background mb-4 text-left font-semibold">Recent Annotation Activity</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {history.slice(0, 3).map((item) => (
                          <div 
                            key={item.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              const resEntry = {
                                filename: item.filename,
                                image: item.thumbnail,
                                detections: item.detections || []
                              };
                              setResult(resEntry);
                              setCustomAnnotations(item.detections || []);
                              persistSession(resEntry, false, [], 0);
                            }}
                            className="bg-surface-container-lowest rounded-xl p-3 shadow-sm hover:shadow-md border border-outline-variant/20 hover:border-primary/50 transition-all flex flex-col group cursor-pointer text-left"
                          >
                            <div className="w-full h-24 rounded-lg overflow-hidden mb-2 relative bg-surface-container">
                              <img className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" src={getImageSrc(item.thumbnail)} alt={item.filename}/>
                            </div>
                            <h5 className="font-body-sm text-[12px] font-semibold text-on-background truncate" title={item.filename}>{item.filename}</h5>
                            <p className="font-label-code text-[10px] text-on-surface-variant">{item.date}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Loaded annotation preview image
                <div 
                  onClick={handleImageClick}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  ref={imageRef}
                  className="relative select-none"
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                    cursor: (activeTool === "rect" || activeTool === "resize") ? "crosshair" : "default"
                  }}
                >
                  <img 
                    src={getImageSrc(result.image)} 
                    alt="Annotation Source" 
                    className="max-h-[460px] object-contain"
                  />
                  
                  {/* Real-time drag preview box */}
                  {isDrawingBox && boxStartCoords && boxCurrentCoords && (
                    <div 
                      className="absolute border-2 border-primary bg-primary/10 pointer-events-none z-30"
                      style={{
                        left: `${Math.min(boxStartCoords[0], boxCurrentCoords[0])}%`,
                        top: `${Math.min(boxStartCoords[1], boxCurrentCoords[1])}%`,
                        width: `${Math.abs(boxCurrentCoords[0] - boxStartCoords[0])}%`,
                        height: `${Math.abs(boxCurrentCoords[1] - boxStartCoords[1])}%`
                      }}
                    />
                  )}
                  
                  {/* Render current active polygon path if drawing */}
                  {currentPolygonPoints.length > 0 && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none z-30" viewBox="0 0 100 100" preserveAspectRatio="none">
                      {/* Lines connecting points */}
                      <polyline 
                        points={currentPolygonPoints.map(p => `${p[0]},${p[1]}`).join(" ")}
                        fill="none"
                        stroke="#2196f3"
                        strokeWidth="1"
                        strokeDasharray="2"
                      />
                      {/* Close line to first point */}
                      {currentPolygonPoints.length >= 3 && (
                        <line 
                          x1={currentPolygonPoints[currentPolygonPoints.length - 1][0]}
                          y1={currentPolygonPoints[currentPolygonPoints.length - 1][1]}
                          x2={currentPolygonPoints[0][0]}
                          y2={currentPolygonPoints[0][1]}
                          stroke="#2196f3"
                          strokeWidth="1"
                          strokeDasharray="2"
                        />
                      )}
                      {/* Vertex circles */}
                      {currentPolygonPoints.map((p, pidx) => (
                        <circle 
                          key={pidx}
                          cx={p[0]}
                          cy={p[1]}
                          r="1.2"
                          fill={pidx === 0 ? "#4caf50" : "#2196f3"}
                          stroke="white"
                          strokeWidth="0.3"
                          className={pidx === 0 ? "animate-pulse" : ""}
                        />
                      ))}
                    </svg>
                  )}

                  {/* Bounding box annotations */}
                  {customAnnotations.map((anno, idx) => {
                    const [x1, y1, x2, y2] = anno.box || [10, 10, 30, 30];
                    const left = Math.min(x1, x2);
                    const top = Math.min(y1, y2);
                    const width = Math.abs(x2 - x1);
                    const height = Math.abs(y2 - y1);
                    const isSelected = selectedAnnotationIndex === idx;
                    return (
                      <div 
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAnnotationIndex(idx);
                        }}
                        className={`absolute group/box transition-all ${
                          isSelected 
                            ? "border-[2px] border-primary bg-primary/10 z-40 ring-1 ring-primary/30" 
                            : anno.points && anno.points.length >= 3 
                              ? "border border-dashed border-primary-container/30" 
                              : "border-[1.5px] border-primary-container bg-primary-container/10"
                        }`}
                        style={{
                          left: `${left}%`,
                          top: `${top}%`,
                          width: `${width || 10}%`,
                          height: `${height || 10}%`
                        }}
                      >
                        {/* If it's a polygon, render its SVG polygon inside the box */}
                        {anno.points && anno.points.length >= 3 && (
                          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <polygon 
                              points={anno.points.map(p => `${((p[0] - left) / (width || 1)) * 100},${((p[1] - top) / (height || 1)) * 100}`).join(" ")}
                              fill={isSelected ? "rgba(76, 175, 80, 0.25)" : "rgba(76, 175, 80, 0.15)"}
                              stroke={isSelected ? "#2196f3" : "#4caf50"}
                              strokeWidth={isSelected ? "2.5" : "1.5"}
                            />
                          </svg>
                        )}
                        <div className="absolute -top-5 left-0 bg-primary-container text-on-primary font-label-code text-[9px] px-1 py-0.5 rounded-sm whitespace-nowrap shadow-sm z-50">
                          {anno.label}
                        </div>
                        
                        {/* Resize Handles (quadrilateral boxes only) */}
                        {activeTool === "resize" && (!anno.points || anno.points.length === 0) && (
                          <>
                            {/* Top-Left */}
                            <div 
                              onMouseDown={(e) => handleResizeMouseDown(e, idx, 'tl')}
                              className="absolute top-0 left-0 w-3 h-3 bg-primary border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize z-50 hover:scale-125 transition-transform"
                            />
                            {/* Top-Right */}
                            <div 
                              onMouseDown={(e) => handleResizeMouseDown(e, idx, 'tr')}
                              className="absolute top-0 right-0 w-3 h-3 bg-primary border-2 border-white rounded-full translate-x-1/2 -translate-y-1/2 cursor-nesw-resize z-50 hover:scale-125 transition-transform"
                            />
                            {/* Bottom-Left */}
                            <div 
                              onMouseDown={(e) => handleResizeMouseDown(e, idx, 'bl')}
                              className="absolute bottom-0 left-0 w-3 h-3 bg-primary border-2 border-white rounded-full -translate-x-1/2 translate-y-1/2 cursor-nesw-resize z-50 hover:scale-125 transition-transform"
                            />
                            {/* Bottom-Right */}
                            <div 
                              onMouseDown={(e) => handleResizeMouseDown(e, idx, 'br')}
                              className="absolute bottom-0 right-0 w-3 h-3 bg-primary border-2 border-white rounded-full translate-x-1/2 translate-y-1/2 cursor-nwse-resize z-50 hover:scale-125 transition-transform"
                            />
                          </>
                        )}
                        {/* Remove button directly on hover */}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            const updated = customAnnotations.filter((_, i) => i !== idx);
                            setCustomAnnotations(updated);
                            if (isFolderMode) {
                              const updatedResults = [...folderResults];
                              updatedResults[activeIndex].detections = updated;
                              setFolderResults(updatedResults);
                            }
                          }}
                          className="absolute -top-2.5 -right-2.5 w-5 h-5 rounded-full bg-error text-white hover:bg-error-hover flex items-center justify-center shadow-md scale-0 group-hover/box:scale-100 transition-transform duration-200 z-30"
                          title="Remove Annotation"
                        >
                          <span className="material-symbols-outlined text-[12px] font-bold">close</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Folder Carousel */}
            {isFolderMode && folderResults.length > 0 && (
              <div className="h-[88px] flex items-center gap-3 overflow-x-auto shrink-0 pb-1 pt-1 px-1 bg-surface-container-low/40 rounded-xl p-2">
                <button 
                  onClick={() => handleSelectFolderImage(Math.max(activeIndex - 1, 0))}
                  className="w-8 h-full shrink-0 flex items-center justify-center rounded-lg hover:bg-surface-container text-on-surface-variant disabled:opacity-30"
                  disabled={activeIndex === 0}
                >
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <div className="flex-1 overflow-x-auto flex gap-stack-sm py-1 snap-x hide-scrollbar">
                  {folderResults.map((item, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => handleSelectFolderImage(idx)}
                      className={`relative w-24 h-16 shrink-0 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${idx === activeIndex ? "border-primary-container scale-105" : "border-outline-variant/40 opacity-60 hover:opacity-100"}`}
                    >
                      <img src={getImageSrc(item.image)} alt="" className="w-full h-full object-cover"/>
                    </div>
                  ))}
                </div>
                <button 
                  onClick={() => handleSelectFolderImage(Math.min(activeIndex + 1, folderResults.length - 1))}
                  className="w-8 h-full shrink-0 flex items-center justify-center rounded-lg hover:bg-surface-container text-on-surface-variant disabled:opacity-30"
                  disabled={activeIndex === folderResults.length - 1}
                >
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
          </div>

          {/* Right Panel: Class lists, Object manager */}
          <aside className="w-full lg:w-[320px] bg-surface-container-lowest border-t lg:border-t-0 lg:border-l border-outline-variant/30 p-gutter flex flex-col gap-stack-lg overflow-y-auto shrink-0 shadow-[0_-4px_20px_rgba(0,0,0,0.02)] lg:shadow-[-4px_0_20px_rgba(0,0,0,0.02)] z-10 justify-between">
            <div className="flex flex-col gap-6">
              {/* Class Legend / selector */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-body-lg font-semibold text-on-surface">Active Annotation Class</h3>
                  <span className="text-[10px] font-mono text-primary bg-primary/10 px-2 py-0.5 rounded-full font-semibold">
                    {classes.length} {classes.length === 1 ? "Class" : "Classes"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto pr-1">
                  {classes.map((cls, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedClass(cls)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border ${selectedClass === cls ? "bg-primary border-primary text-on-primary shadow-sm" : "border-outline-variant/50 hover:bg-surface-container text-on-surface-variant"}`}
                    >
                      {cls}
                    </button>
                  ))}
                </div>
              </div>

              {/* Class Manager Input */}
              <div className="border-t border-outline-variant/20 pt-4 flex flex-col gap-2">
                <h4 className="font-body-sm font-semibold text-on-surface">Add New Label Category</h4>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    placeholder="New label..."
                    value={newLabelInput}
                    onChange={(e) => setNewLabelInput(e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-outline-variant/60 rounded-lg text-body-sm focus:outline-none"
                  />
                  <button onClick={handleAddLabel} className="bg-primary text-on-primary px-3 py-1.5 rounded-lg text-body-sm">Add</button>
                </div>
                <div className="flex flex-col gap-2 mt-2">
                  <button onClick={handleUpdateYaml} className="w-full border border-outline-variant hover:bg-surface-container text-primary font-semibold py-1.5 rounded-lg text-label-code">
                    Save Config (dataset.yaml)
                  </button>
                  <button onClick={handleDownloadZip} className="w-full border border-outline-variant hover:bg-surface-container text-primary font-semibold py-1.5 rounded-lg text-label-code">
                    Download Annotations (ZIP)
                  </button>
                </div>
              </div>

              {/* YOLO Detection Controls Panel */}
              <div className="border-t border-outline-variant/20 pt-4 flex flex-col gap-3 bg-primary/5 rounded-xl p-3 border border-primary/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-amber-500 text-[18px]">bolt</span>
                    <h4 className="font-body-sm font-semibold text-on-surface">YOLO Pest Auto-Detect</h4>
                  </div>
                  <span className="text-[10px] font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                    conf: {yoloConf.toFixed(2)}
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <input
                    type="range"
                    min="0.02"
                    max="0.50"
                    step="0.01"
                    value={yoloConf}
                    onChange={(e) => setYoloConf(parseFloat(e.target.value))}
                    className="w-full h-1.5 bg-outline-variant/50 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-[10px] text-on-surface-variant font-mono">
                    <button type="button" onClick={() => setYoloConf(0.05)} className={`hover:text-primary ${yoloConf === 0.05 ? "font-bold text-primary" : ""}`}>5%</button>
                    <button type="button" onClick={() => setYoloConf(0.08)} className={`hover:text-primary ${yoloConf === 0.08 ? "font-bold text-primary" : ""}`}>8%</button>
                    <button type="button" onClick={() => setYoloConf(0.15)} className={`hover:text-primary ${yoloConf === 0.15 ? "font-bold text-primary" : ""}`}>15%</button>
                    <button type="button" onClick={() => setYoloConf(0.25)} className={`hover:text-primary ${yoloConf === 0.25 ? "font-bold text-primary" : ""}`}>25%</button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleRunYolo()}
                  disabled={isDetecting || loading}
                  className={`w-full py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 shadow-xs transition-all ${
                    isDetecting
                      ? "bg-amber-500/20 text-amber-600 cursor-wait animate-pulse"
                      : "bg-gradient-to-r from-primary to-primary-container text-on-primary hover:shadow-md cursor-pointer"
                  }`}
                >
                  <span className="material-symbols-outlined text-[16px]">
                    {isDetecting ? "sync" : "smart_toy"}
                  </span>
                  <span>{isDetecting ? "Running YOLO + SAM2..." : "Run YOLO Auto-Detect"}</span>
                </button>
              </div>

              {/* Bounding box list */}
              {result && (
                <div className="border-t border-outline-variant/20 pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-body-sm font-semibold text-on-surface">Annotations ({customAnnotations.length})</h3>
                    <button onClick={() => setCustomAnnotations([])} className="text-error text-label-code hover:underline">Clear all</button>
                  </div>
                  <div className="max-h-[180px] overflow-y-auto flex flex-col gap-2">
                    {customAnnotations.map((anno, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => setSelectedAnnotationIndex(idx)}
                        className={`flex justify-between items-center rounded-lg p-2 text-body-sm border transition-all cursor-pointer ${
                          selectedAnnotationIndex === idx 
                            ? "bg-primary/5 border-primary shadow-xs ring-1 ring-primary/20" 
                            : "bg-surface-container border-outline-variant/10 hover:border-outline-variant/30"
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          {anno.points && anno.points.length >= 3 && (
                            <span className="material-symbols-outlined text-[14px] text-emerald-500 shrink-0" title="SAM2 Segmented Polygon">polyline</span>
                          )}
                          <input 
                            type="text"
                            value={anno.label}
                            onChange={(e) => {
                              const val = e.target.value;
                              const updated = [...customAnnotations];
                              updated[idx] = { ...updated[idx], label: val };
                              setCustomAnnotations(updated);
                              if (isFolderMode) {
                                const updatedResults = [...folderResults];
                                updatedResults[activeIndex].detections = updated;
                                setFolderResults(updatedResults);
                              }
                            }}
                            className="bg-surface-container-highest border border-outline-variant/40 rounded px-1.5 py-0.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary w-24"
                            title="Edit label name"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`font-label-code text-[10px] px-1.5 py-0.5 rounded font-bold ${
                            (anno.confidence || 1.0) >= 0.7 
                              ? "bg-emerald-500/10 text-emerald-600" 
                              : (anno.confidence || 1.0) >= 0.4 
                                ? "bg-amber-500/10 text-amber-600" 
                                : "bg-primary/10 text-primary"
                          }`}>
                            {((anno.confidence !== undefined && anno.confidence !== null ? anno.confidence : 1.0) * 100).toFixed(0)}%
                          </span>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              const updated = customAnnotations.filter((_, i) => i !== idx);
                              setCustomAnnotations(updated);
                              if (isFolderMode) {
                                const updatedResults = [...folderResults];
                                updatedResults[activeIndex].detections = updated;
                                setFolderResults(updatedResults);
                              }
                            }}
                            className="text-error hover:text-error/80"
                            title="Delete Annotation"
                          >
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Actions */}
            {result && (
              <div className="border-t border-outline-variant/20 pt-4 flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={downloadJSON}
                    className="bg-primary text-on-primary hover:bg-primary/95 py-2.5 rounded-lg font-body-sm font-semibold flex items-center justify-center gap-1.5 shadow-sm"
                  >
                    <span className="material-symbols-outlined text-[18px]">download</span> Export JSON
                  </button>
                  <button 
                    onClick={downloadCSV}
                    className="bg-primary text-on-primary hover:bg-primary/95 py-2.5 rounded-lg font-body-sm font-semibold flex items-center justify-center gap-1.5 shadow-sm"
                  >
                    <span className="material-symbols-outlined text-[18px]">table_rows</span> Export CSV
                  </button>
                </div>
                {isFolderMode && (
                  <div className="grid grid-cols-2 gap-2">
                    <button 
                      onClick={downloadBatchJSON}
                      className="border border-outline-variant hover:bg-surface-container text-primary py-2.5 rounded-lg font-body-sm font-semibold flex items-center justify-center gap-1.5"
                    >
                      <span className="material-symbols-outlined text-[18px]">data_object</span> Batch JSON
                    </button>
                    <button 
                      onClick={downloadBatchCSV}
                      className="border border-outline-variant hover:bg-surface-container text-primary py-2.5 rounded-lg font-body-sm font-semibold flex items-center justify-center gap-1.5"
                    >
                      <span className="material-symbols-outlined text-[18px]">table_chart</span> Batch CSV
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button 
                    onClick={() => anotherFileInputRef.current?.click()}
                    className="border border-outline-variant bg-transparent text-primary hover:bg-primary/5 py-2.5 rounded-lg font-label-code text-label-code flex items-center justify-center gap-1.5 font-semibold"
                  >
                    <span className="material-symbols-outlined text-[16px]">add_a_photo</span> Image
                  </button>
                  <button 
                    onClick={() => anotherFolderInputRef.current?.click()}
                    className="border border-outline-variant bg-transparent text-primary hover:bg-primary/5 py-2.5 rounded-lg font-label-code text-label-code flex items-center justify-center gap-1.5 font-semibold"
                  >
                    <span className="material-symbols-outlined text-[16px]">create_new_folder</span> Folder
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* Hidden inputs rendered globally (Always in DOM) */}
      <input 
        type="file" 
        accept="image/*" 
        ref={anotherFileInputRef} 
        className="hidden" 
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            handleUploadSingleImage(e.target.files[0]);
          }
        }} 
      />
      <input 
        type="file" 
        webkitdirectory="true" 
        directory="true" 
        multiple 
        ref={anotherFolderInputRef} 
        className="hidden" 
        onChange={handleFolderUpload} 
      />
      <VerifiedReportsModal
        isOpen={showVerifiedModal}
        onClose={() => setShowVerifiedModal(false)}
        tickets={userTickets}
      />
    </div>
  );
}
